import React, { useEffect, useMemo, useState } from "react";
import { Icon, Field, PrimaryButton } from "@/components/kit";
import { api } from "@/api";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";

const L = {
  fr: {
    title: "CRM Abonnements", sub: "Suivi des abonnements des professionnels",
    active: "Actif", trial: "Essai", expired: "Expiré", none: "Aucun",
    total_paid: "Total encaissé", pros: "professionnels",
    renewals: "Calendrier des renouvellements", no_renewals: "Aucun renouvellement planifié.",
    overdue: "En retard", days_left: "j restants", today: "aujourd'hui",
    manage: "Gérer l'abonnement", plan: "Formule", monthly: "Mensuel", yearly: "Annuel",
    amount: "Montant (€)", start: "Date de début", end: "Date de fin / renouvellement",
    force_trial: "Forcer le statut « Essai »", notes: "Notes", save: "Enregistrer l'abonnement",
    saved: "Abonnement enregistré ✓", payments: "Historique des paiements",
    add_payment: "Ajouter un paiement", p_amount: "Montant (€)", p_date: "Date", p_method: "Méthode",
    p_note: "Note", p_add: "Ajouter", no_payments: "Aucun paiement enregistré.",
    del_p: "Supprimer ce paiement ?", empty: "Aucun compte professionnel.",
    m_virement: "Virement", m_carte: "Carte", m_especes: "Espèces", m_autre: "Autre",
    renews_on: "Renouvellement", no_plan: "Non défini",
    add_pro: "Ajouter un professionnel", cf_name: "Nom de l'entreprise", cf_email: "Email de connexion",
    cf_phone: "Téléphone", cf_vat: "N° TVA", cf_logo: "Logo (png/jpg/webp)", cf_pw: "Mot de passe (min. 6)",
    cf_status: "Statut", st_trial: "Essai", st_active: "Actif", st_expired: "Expiré", free: "Gratuit (essai)",
    usage: "Essais utilisés", u_est: "estimations", u_dev: "devis",
    cf_create: "Créer le professionnel", cf_gen: "Régénérer", cf_choose: "Choisir un fichier", cf_nofile: "Aucun fichier",
    creds_title: "Identifiants du compte créé", creds_hint: "Copiez et transmettez ces informations au professionnel.",
    creds_close: "Fermer", creds_copy: "Copier", creds_copied: "Copié ✓", creds_link: "Lien public", created_ok: "Professionnel créé ✓",
  },
  nl: {
    title: "Abonnementen-CRM", sub: "Opvolging van de abonnementen van professionals",
    active: "Actief", trial: "Proef", expired: "Verlopen", none: "Geen",
    total_paid: "Totaal ontvangen", pros: "professionals",
    renewals: "Verlengingskalender", no_renewals: "Geen verlenging gepland.",
    overdue: "Te laat", days_left: "d resterend", today: "vandaag",
    manage: "Abonnement beheren", plan: "Formule", monthly: "Maandelijks", yearly: "Jaarlijks",
    amount: "Bedrag (€)", start: "Startdatum", end: "Einddatum / verlenging",
    force_trial: "Status « Proef » forceren", notes: "Notities", save: "Abonnement opslaan",
    saved: "Abonnement opgeslagen ✓", payments: "Betalingsgeschiedenis",
    add_payment: "Betaling toevoegen", p_amount: "Bedrag (€)", p_date: "Datum", p_method: "Methode",
    p_note: "Notitie", p_add: "Toevoegen", no_payments: "Geen betalingen geregistreerd.",
    del_p: "Deze betaling verwijderen?", empty: "Geen professionele accounts.",
    m_virement: "Overschrijving", m_carte: "Kaart", m_especes: "Contant", m_autre: "Andere",
    renews_on: "Verlenging", no_plan: "Niet ingesteld",
    add_pro: "Professional toevoegen", cf_name: "Bedrijfsnaam", cf_email: "Login e-mail",
    cf_phone: "Telefoon", cf_vat: "Btw-nr.", cf_logo: "Logo (png/jpg/webp)", cf_pw: "Wachtwoord (min. 6)",
    cf_status: "Status", st_trial: "Proef", st_active: "Actief", st_expired: "Verlopen", free: "Gratis (proef)",
    usage: "Proeven gebruikt", u_est: "ramingen", u_dev: "offertes",
    cf_create: "Professional aanmaken", cf_gen: "Opnieuw", cf_choose: "Bestand kiezen", cf_nofile: "Geen bestand",
    creds_title: "Inloggegevens van het aangemaakte account", creds_hint: "Kopieer en deel deze gegevens met de professional.",
    creds_close: "Sluiten", creds_copy: "Kopiëren", creds_copied: "Gekopieerd ✓", creds_link: "Openbare link", created_ok: "Professional aangemaakt ✓",
  },
  en: {
    title: "Subscriptions CRM", sub: "Track professionals' subscriptions",
    active: "Active", trial: "Trial", expired: "Expired", none: "None",
    total_paid: "Total collected", pros: "professionals",
    renewals: "Renewals calendar", no_renewals: "No renewal scheduled.",
    overdue: "Overdue", days_left: "d left", today: "today",
    manage: "Manage subscription", plan: "Plan", monthly: "Monthly", yearly: "Yearly",
    amount: "Amount (€)", start: "Start date", end: "End / renewal date",
    force_trial: "Force “Trial” status", notes: "Notes", save: "Save subscription",
    saved: "Subscription saved ✓", payments: "Payment history",
    add_payment: "Add a payment", p_amount: "Amount (€)", p_date: "Date", p_method: "Method",
    p_note: "Note", p_add: "Add", no_payments: "No payment recorded.",
    del_p: "Delete this payment?", empty: "No professional accounts.",
    m_virement: "Bank transfer", m_carte: "Card", m_especes: "Cash", m_autre: "Other",
    renews_on: "Renewal", no_plan: "Not set",
    add_pro: "Add a professional", cf_name: "Company name", cf_email: "Login email",
    cf_phone: "Phone", cf_vat: "VAT no.", cf_logo: "Logo (png/jpg/webp)", cf_pw: "Password (min. 6)",
    cf_status: "Status", st_trial: "Trial", st_active: "Active", st_expired: "Expired", free: "Free (trial)",
    usage: "Trials used", u_est: "estimates", u_dev: "quotes",
    cf_create: "Create professional", cf_gen: "Regenerate", cf_choose: "Choose a file", cf_nofile: "No file",
    creds_title: "Created account credentials", creds_hint: "Copy and share these details with the professional.",
    creds_close: "Close", creds_copy: "Copy", creds_copied: "Copied ✓", creds_link: "Public link", created_ok: "Professional created ✓",
  },
  de: {
    title: "Abo-CRM", sub: "Verfolgung der Abonnements der Fachleute",
    active: "Aktiv", trial: "Test", expired: "Abgelaufen", none: "Keins",
    total_paid: "Gesamt eingenommen", pros: "Fachleute",
    renewals: "Verlängerungskalender", no_renewals: "Keine Verlängerung geplant.",
    overdue: "Überfällig", days_left: "T übrig", today: "heute",
    manage: "Abo verwalten", plan: "Formel", monthly: "Monatlich", yearly: "Jährlich",
    amount: "Betrag (€)", start: "Startdatum", end: "Enddatum / Verlängerung",
    force_trial: "Status „Test“ erzwingen", notes: "Notizen", save: "Abo speichern",
    saved: "Abo gespeichert ✓", payments: "Zahlungsverlauf",
    add_payment: "Zahlung hinzufügen", p_amount: "Betrag (€)", p_date: "Datum", p_method: "Methode",
    p_note: "Notiz", p_add: "Hinzufügen", no_payments: "Keine Zahlung erfasst.",
    del_p: "Diese Zahlung löschen?", empty: "Keine Profi-Konten.",
    m_virement: "Überweisung", m_carte: "Karte", m_especes: "Bar", m_autre: "Andere",
    renews_on: "Verlängerung", no_plan: "Nicht festgelegt",
    add_pro: "Fachmann hinzufügen", cf_name: "Firmenname", cf_email: "Login-E-Mail",
    cf_phone: "Telefon", cf_vat: "USt-IdNr.", cf_logo: "Logo (png/jpg/webp)", cf_pw: "Passwort (min. 6)",
    cf_status: "Status", st_trial: "Test", st_active: "Aktiv", st_expired: "Abgelaufen", free: "Kostenlos (Test)",
    usage: "Tests genutzt", u_est: "Schätzungen", u_dev: "Angebote",
    cf_create: "Fachmann erstellen", cf_gen: "Neu", cf_choose: "Datei wählen", cf_nofile: "Keine Datei",
    creds_title: "Zugangsdaten des erstellten Kontos", creds_hint: "Kopieren Sie diese Angaben und geben Sie sie an den Fachmann weiter.",
    creds_close: "Schließen", creds_copy: "Kopieren", creds_copied: "Kopiert ✓", creds_link: "Öffentlicher Link", created_ok: "Fachmann erstellt ✓",
  },
};

const STATUS_STYLE = {
  active: "bg-[#EAF0EB] text-ok",
  trial: "bg-[#EDE7F3] text-[#6A4FA3]",
  expired: "bg-[#F3E7E6] text-err",
  none: "bg-paper-3 text-ink-3",
};

const eur = (n) => (Math.round((n || 0) * 100) / 100).toLocaleString("fr-FR");
const genPw = () =>
  Math.random().toString(36).slice(2, 6) +
  Math.random().toString(36).slice(2, 6).toUpperCase() +
  Math.floor(10 + Math.random() * 89);
const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
};

export default function Subscriptions() {
  const { lang } = useI18n();
  const tt = L[lang] || L.fr;
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const emptyForm = () => ({
    name: "", email: "", phone: "", vat: "", password: genPw(),
    start_date: "", end_date: "", plan: "monthly", amount: "", notes: "",
  });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [logoFile, setLogoFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [cError, setCError] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const createPro = async () => {
    setCreating(true); setCError("");
    try {
      const r = await api.createUser({
        email: form.email.trim(), password: form.password.trim(),
        company_name: form.name.trim(), phone: form.phone.trim(), vat: form.vat.trim(),
      });
      const uid = r.user.id;
      const isTrial = form.plan === "trial";
      await api.setSubscription(uid, {
        plan: form.plan, amount: isTrial ? 0 : (form.amount === "" ? null : Number(form.amount)),
        start_date: form.start_date || null, end_date: form.end_date || null,
        trial: isTrial, notes: form.notes,
      });
      if (logoFile) {
        try { await api.uploadLogoFor(uid, logoFile); } catch (e) { /* logo optional */ }
      }
      setCreated({ email: form.email.trim(), password: form.password.trim(), slug: r.user.slug });
      setShowCreate(false); setForm(emptyForm()); setLogoFile(null);
      await load();
    } catch (e) { setCError(e.message); } finally { setCreating(false); }
  };

  const load = async () => {
    setLoading(true);
    try { const d = await api.listSubscriptions(); setPros(d.pros); setError(""); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { active: 0, trial: 0, expired: 0, none: 0, total: 0 };
    pros.forEach((p) => { c[p.sub_status] = (c[p.sub_status] || 0) + 1; c.total += p.total_paid || 0; });
    return c;
  }, [pros]);

  const renewals = useMemo(() => {
    return pros
      .filter((p) => p.subscription?.end_date && !p.subscription?.trial)
      .map((p) => ({ ...p, dleft: daysUntil(p.subscription.end_date) }))
      .sort((a, b) => a.dleft - b.dleft);
  }, [pros]);

  return (
    <div className="mx-auto max-w-5xl px-5 py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl font-semibold text-ink">{tt.title}</h1>
          <p className="mt-1 text-[14px] text-mute">{tt.sub}</p>
        </div>
        <button data-testid="sub-add-toggle" onClick={() => { setShowCreate((s) => !s); setCError(""); }}
          className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-[14px] font-semibold text-white">
          <Icon name={showCreate ? "x" : "plus"} size={17} className="text-white" /> {tt.add_pro}
        </button>
      </div>

      {showCreate && (
        <div className="mb-6 rounded-2xl border border-line bg-white p-5" data-testid="sub-create-panel">
          <div className="grid gap-x-4 sm:grid-cols-2">
            <Field label={tt.cf_name} testId="cf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Peinture Martin" />
            <Field label={tt.cf_email} testId="cf-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="pro@email.be" />
            <Field label={tt.cf_phone} testId="cf-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="02 672 14 36" />
            <Field label={tt.cf_vat} testId="cf-vat" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} placeholder="BE0123.456.789" />
            <div className="mb-4">
              <span className="mb-1.5 block text-[13px] font-medium text-ink-3">{tt.cf_pw}</span>
              <div className="flex gap-2">
                <input data-testid="cf-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="field-input flex-1" />
                <button type="button" data-testid="cf-genpw" onClick={() => setForm({ ...form, password: genPw() })} className="shrink-0 rounded-lg border border-line px-3 text-[13px] font-medium text-ink-3">{tt.cf_gen}</button>
              </div>
            </div>
            <div className="mb-4">
              <span className="mb-1.5 block text-[13px] font-medium text-ink-3">{tt.cf_logo}</span>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-line px-3 py-2.5 text-[13px] text-ink-3">
                <Icon name="upload" size={15} />
                <span className="truncate">{logoFile ? logoFile.name : tt.cf_nofile}</span>
                <input data-testid="cf-logo" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <label className="mb-4 block">
              <span className="mb-1.5 block text-[13px] font-medium text-ink-3">{tt.plan}</span>
              <select data-testid="cf-plan" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="field-input">
                <option value="trial">{tt.st_trial}</option>
                <option value="monthly">{tt.monthly}</option>
                <option value="yearly">{tt.yearly}</option>
              </select>
            </label>
            {form.plan === "trial" ? (
              <div className="mb-4 flex items-end">
                <p data-testid="cf-free" className="pb-2.5 text-[14px] font-semibold text-ok">{tt.free}</p>
              </div>
            ) : (
              <Field label={tt.amount} testId="cf-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="49" />
            )}
            <Field label={tt.start} testId="cf-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            <Field label={tt.end} testId="cf-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
          <Field label={tt.notes} testId="cf-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="…" />
          {cError ? <p data-testid="cf-error" className="mb-3 text-[14px] font-medium text-err">{cError}</p> : null}
          <div className="max-w-xs">
            <PrimaryButton testId="cf-create-btn" title={tt.cf_create} icon="check" onClick={createPro} loading={creating} disabled={!form.email || form.password.trim().length < 6} />
          </div>
        </div>
      )}

      {created ? (
        <div data-testid="sub-created-credentials" className="mb-6 rounded-2xl border-[1.5px] border-brand bg-brand-tertiary p-5">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-[15px] font-semibold text-brand-on">
              <Icon name="key" size={16} className="text-brand" /> {tt.creds_title}
            </h3>
            <button data-testid="sub-creds-close" onClick={() => setCreated(null)} className="text-[13px] font-medium text-brand-on">{tt.creds_close}</button>
          </div>
          <p className="mb-3 text-[12px] text-ink-3">{tt.creds_hint}</p>
          <div className="space-y-1 rounded-xl bg-white p-3 text-[14px] text-ink">
            <p><span className="text-mute">{tt.cf_email} :</span> <b>{created.email}</b></p>
            <p><span className="text-mute">{tt.cf_pw} :</span> <b>{created.password}</b></p>
            {created.slug ? <p className="break-all"><span className="text-mute">{tt.creds_link} :</span> <b>{`${window.location.origin}/p/${created.slug}`}</b></p> : null}
          </div>
          <button data-testid="sub-creds-copy"
            onClick={() => { navigator.clipboard?.writeText(`${tt.cf_email}: ${created.email}\n${tt.cf_pw}: ${created.password}\n${tt.creds_link}: ${window.location.origin}/p/${created.slug}`); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
            className="mt-3 flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-[13px] font-semibold text-white">
            <Icon name="copy" size={14} className="text-white" /> {copied ? tt.creds_copied : tt.creds_copy}
          </button>
        </div>
      ) : null}

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat icon="check-circle" label={tt.active} value={counts.active} tone="ok" />
        <Stat icon="gift" label={tt.trial} value={counts.trial} tone="trial" />
        <Stat icon="alert-circle" label={tt.expired} value={counts.expired} tone="err" />
        <Stat icon="euro" label={tt.total_paid} value={`${eur(counts.total)} €`} tone="brand" />
      </div>

      {error ? <p data-testid="subs-error" className="mb-4 text-[14px] font-medium text-err">{error}</p> : null}

      {/* Renewals calendar */}
      <div className="mb-6 rounded-2xl border border-line bg-white p-5" data-testid="renewals-calendar">
        <h2 className="mb-3 flex items-center gap-2 text-[16px] font-semibold text-ink">
          <Icon name="calendar" size={18} className="text-brand" /> {tt.renewals}
        </h2>
        {renewals.length === 0 ? (
          <p className="text-[13px] text-mute">{tt.no_renewals}</p>
        ) : (
          <div className="space-y-2">
            {renewals.map((p) => {
              const late = p.dleft < 0;
              const soon = p.dleft >= 0 && p.dleft <= 30;
              return (
                <div key={p.id} data-testid={`renewal-${p.id}`} className="flex items-center justify-between gap-3 rounded-xl bg-paper-3/50 px-3.5 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-semibold text-ink">{p.company?.company_name || p.email}</p>
                    <p className="text-[12px] text-mute">{tt.renews_on} : {String(p.subscription.end_date).slice(0, 10)}</p>
                  </div>
                  <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-[12px] font-semibold",
                    late ? "bg-[#F3E7E6] text-err" : soon ? "bg-[#F5E9DC] text-warn" : "bg-[#EAF0EB] text-ok")}>
                    {late ? tt.overdue : p.dleft === 0 ? tt.today : `${p.dleft} ${tt.days_left}`}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pro list */}
      {loading ? (
        <div className="flex justify-center py-20"><Icon name="loader" size={32} className="animate-spin text-brand" /></div>
      ) : pros.length === 0 ? (
        <p className="text-[14px] text-mute">{tt.empty}</p>
      ) : (
        <div className="space-y-3">
          {pros.map((p) => (
            <ProRow key={p.id} pro={p} tt={tt} open={expanded === p.id}
              onToggle={() => setExpanded(expanded === p.id ? null : p.id)} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProRow({ pro, tt, open, onToggle, onSaved }) {
  const s = pro.subscription || {};
  const [sub, setSub] = useState({
    plan: (s.plan === "trial" || s.trial) ? "trial" : (s.plan || "monthly"), amount: s.amount ?? "", start_date: (s.start_date || "").slice(0, 10),
    end_date: (s.end_date || "").slice(0, 10), notes: s.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [payments, setPayments] = useState(null);
  const [pform, setPform] = useState({ amount: "", date: "", method: "virement", note: "" });
  const [padding, setPadding] = useState(false);

  const loadPayments = async () => {
    try { const d = await api.listPayments(pro.id); setPayments(d.payments); } catch (e) { /* noop */ }
  };
  useEffect(() => { if (open && payments === null) loadPayments(); }, [open]);

  const save = async () => {
    setSaving(true); setNotice("");
    try {
      const isTrial = sub.plan === "trial";
      await api.setSubscription(pro.id, {
        plan: sub.plan, amount: isTrial ? 0 : (sub.amount === "" ? null : Number(sub.amount)),
        start_date: sub.start_date || null, end_date: sub.end_date || null,
        trial: isTrial, notes: sub.notes,
      });
      setNotice(tt.saved); onSaved();
    } catch (e) { setNotice(e.message); } finally { setSaving(false); }
  };
  const addPay = async () => {
    if (!pform.amount) return;
    setPadding(true);
    try {
      await api.addPayment(pro.id, { amount: Number(pform.amount), date: pform.date || null, method: pform.method, note: pform.note });
      setPform({ amount: "", date: "", method: "virement", note: "" });
      await loadPayments(); onSaved();
    } catch (e) { setNotice(e.message); } finally { setPadding(false); }
  };
  const delPay = async (pid) => {
    if (!window.confirm(tt.del_p)) return;
    await api.deletePayment(pro.id, pid); await loadPayments(); onSaved();
  };

  return (
    <div data-testid={`sub-row-${pro.id}`} className="rounded-2xl border border-line bg-white">
      <div onClick={onToggle} className="flex cursor-pointer flex-wrap items-center justify-between gap-3 p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[16px] font-semibold text-ink">{pro.company?.company_name || pro.email}</p>
            <span data-testid={`sub-status-${pro.id}`} className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", STATUS_STYLE[pro.sub_status])}>
              {tt[pro.sub_status] || tt.none}
            </span>
          </div>
          <p className="text-[13px] text-mute">
            {pro.email}
            {s.end_date && !s.trial ? ` · ${tt.renews_on}: ${String(s.end_date).slice(0, 10)}` : ""}
            {` · ${tt.total_paid}: ${eur(pro.total_paid)} €`}
          </p>
          {pro.sub_status === "trial" ? (
            <p data-testid={`trial-usage-${pro.id}`} className="mt-1 text-[12px] font-medium text-[#6A4FA3]">
              {tt.usage}: {(pro.trial_usage?.estimations ?? 0)}/{2} {tt.u_est} · {(pro.trial_usage?.devis ?? 0)}/{2} {tt.u_dev}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-brand">{tt.manage}</span>
          <Icon name={open ? "chevron-up" : "chevron-down"} size={18} className="text-mute" />
        </div>
      </div>

      {open && (
        <div className="border-t border-divider bg-paper-3/30 p-4">
          {/* Subscription editor */}
          <div className="grid gap-x-4 sm:grid-cols-2">
            <label className="mb-4 block">
              <span className="mb-1.5 block text-[13px] font-medium text-ink-3">{tt.plan}</span>
              <select data-testid={`sub-plan-${pro.id}`} value={sub.plan} onChange={(e) => setSub({ ...sub, plan: e.target.value })} className="field-input">
                <option value="trial">{tt.st_trial}</option>
                <option value="monthly">{tt.monthly}</option>
                <option value="yearly">{tt.yearly}</option>
              </select>
            </label>
            {sub.plan === "trial" ? (
              <div className="mb-4 flex items-end">
                <p data-testid={`sub-free-${pro.id}`} className="pb-2.5 text-[14px] font-semibold text-ok">{tt.free}</p>
              </div>
            ) : (
              <Field label={tt.amount} testId={`sub-amount-${pro.id}`} type="number" value={sub.amount} onChange={(e) => setSub({ ...sub, amount: e.target.value })} placeholder="49" />
            )}
            <Field label={tt.start} testId={`sub-start-${pro.id}`} type="date" value={sub.start_date} onChange={(e) => setSub({ ...sub, start_date: e.target.value })} />
            <Field label={tt.end} testId={`sub-end-${pro.id}`} type="date" value={sub.end_date} onChange={(e) => setSub({ ...sub, end_date: e.target.value })} />
          </div>
          <Field label={tt.notes} testId={`sub-notes-${pro.id}`} value={sub.notes} onChange={(e) => setSub({ ...sub, notes: e.target.value })} placeholder="…" />
          <div className="max-w-xs">
            <PrimaryButton testId={`sub-save-${pro.id}`} title={tt.save} icon="check" onClick={save} loading={saving} />
          </div>
          {notice ? <p data-testid={`sub-notice-${pro.id}`} className="mt-2 text-[13px] font-medium text-ok">{notice}</p> : null}

          {/* Payment history */}
          <div className="mt-5 border-t border-divider pt-4">
            <h3 className="mb-3 flex items-center gap-2 text-[15px] font-semibold text-ink">
              <Icon name="credit-card" size={16} className="text-brand" /> {tt.payments}
            </h3>
            {payments === null ? (
              <Icon name="loader" size={20} className="animate-spin text-brand" />
            ) : payments.length === 0 ? (
              <p className="mb-3 text-[13px] text-mute">{tt.no_payments}</p>
            ) : (
              <div className="mb-3 space-y-1.5">
                {payments.map((p) => (
                  <div key={p.id} data-testid={`payment-${p.id}`} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-[13px]">
                    <span className="text-ink">
                      <b>{eur(p.amount)} €</b> · {String(p.date).slice(0, 10)}
                      {p.method ? ` · ${tt[`m_${p.method}`] || p.method}` : ""}
                      {p.note ? ` · ${p.note}` : ""}
                    </span>
                    <button data-testid={`payment-del-${p.id}`} onClick={() => delPay(p.id)} className="text-err"><Icon name="trash-2" size={15} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid items-end gap-x-3 sm:grid-cols-[1fr_1fr_1fr_1.4fr_auto]">
              <Field label={tt.p_amount} testId={`pay-amount-${pro.id}`} type="number" value={pform.amount} onChange={(e) => setPform({ ...pform, amount: e.target.value })} placeholder="49" />
              <Field label={tt.p_date} testId={`pay-date-${pro.id}`} type="date" value={pform.date} onChange={(e) => setPform({ ...pform, date: e.target.value })} />
              <label className="mb-4 block">
                <span className="mb-1.5 block text-[13px] font-medium text-ink-3">{tt.p_method}</span>
                <select data-testid={`pay-method-${pro.id}`} value={pform.method} onChange={(e) => setPform({ ...pform, method: e.target.value })} className="field-input">
                  <option value="virement">{tt.m_virement}</option>
                  <option value="carte">{tt.m_carte}</option>
                  <option value="especes">{tt.m_especes}</option>
                  <option value="autre">{tt.m_autre}</option>
                </select>
              </label>
              <Field label={tt.p_note} testId={`pay-note-${pro.id}`} value={pform.note} onChange={(e) => setPform({ ...pform, note: e.target.value })} placeholder="…" />
              <button data-testid={`pay-add-${pro.id}`} onClick={addPay} disabled={!pform.amount || padding}
                className="mb-4 flex h-[42px] items-center gap-1.5 rounded-lg bg-brand px-4 text-[14px] font-semibold text-white disabled:opacity-50">
                <Icon name="plus" size={15} className="text-white" /> {tt.p_add}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TONE = {
  ok: "bg-[#EAF0EB] text-ok", trial: "bg-[#EDE7F3] text-[#6A4FA3]",
  err: "bg-[#F3E7E6] text-err", brand: "bg-brand-tertiary text-brand",
};
const Stat = ({ icon, label, value, tone }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-line bg-white p-4">
    <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl", TONE[tone])}>
      <Icon name={icon} size={20} />
    </span>
    <div className="min-w-0">
      <p className="font-display text-2xl font-bold text-ink">{value}</p>
      <p className="truncate text-[12px] text-mute">{label}</p>
    </div>
  </div>
);
